import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { IF } from "../../../../../../components/hoc/If";
import { useCurrentOrganisationId } from "../../../../../../services/local/organisationContext/OrganisationContextModel";
import { useFlightPathUser } from "../../../../../auth_insight";
import { AppGridToolbarType } from "../../types/AppGrid_types";
import { AppGridToolbar } from "../base/AppGridToolbar_view";
import { AppGrid } from "../base/AppGrid_view";
import { ModalGridToolbar } from "../base/ModalGridToolbar_view";
import { PhaseGridModel } from "./PhasesGridView_model";
import { EntityTypes, GridTypes } from "../../../../../../enums";
import PhaseDetailCellRenderer from "./PhaseDetailCellRenderer/PhaseDetailCellRenderer_view";
import Pages from "../../../../../../routes/InsightRoutes";
import { EmptyPhaseGridTemplate } from "./templates/EmptyPhaseDataTemplate/EmptyPhaseDataTemplate";

interface PhasesGridProps {
  data: any[];
  gridToolbarType?: AppGridToolbarType;
  onAssignSuccess?: (selectedItems: number[]) => void;
  selectedLinkItems?: number[];
  refreshDataHandler?: any;
  connectedUsers?: FP.Entities.IUser[];
  onFieldUpdate?: () => void;
  onCellStateChange?: (organisationId: number, projectId: number) => void;
  preSelectedItemIds?: number[];
  gridType?: EntityTypes | GridTypes;
  showAudiences?: boolean; // this is the prop that determines if it's in the audiences view or the projectsettings view
}

export const PhasesGrid: React.FunctionComponent<PhasesGridProps> = observer(
  ({
    data,
    gridToolbarType = "base",
    onAssignSuccess,
    connectedUsers,
    refreshDataHandler,
    onFieldUpdate,
    onCellStateChange,
    preSelectedItemIds,
    gridType,
    showAudiences
  }) => {
    const organisationId = useCurrentOrganisationId();
    const authUser = useFlightPathUser();
    const gridRef = useRef<AppGrid>();
    const [searchParams] = useSearchParams();
    const { projectId } = useParams<{ projectId: string }>();

    const [gridModel, setGridModel] = useState(
      () =>
        new PhaseGridModel(
          organisationId,
          +projectId,
          authUser,
          refreshDataHandler,
          onFieldUpdate,
          gridToolbarType,
          preSelectedItemIds,
          gridType,
          searchParams,
          showAudiences
        )
    );

    useEffect(() => {
      if (
        (typeof projectId !== "undefined" && gridModel.projectId !== +projectId) ||
        gridModel.organisationId !== organisationId
      ) {
        setGridModel(
          new PhaseGridModel(
            organisationId,
            +projectId,
            authUser,
            refreshDataHandler,
            onFieldUpdate,
            gridToolbarType,
            preSelectedItemIds,
            gridType,
            searchParams,
            showAudiences
          )
        );
      }
      // eslint-disable-next-line
    }, [organisationId, projectId]);

    useEffect(() => {
      gridModel.onMount();
      return gridModel.onUnmount;
      // eslint-disable-next-line
    }, []);

    return (
      <>
        <IF condition={!!data}>
          <IF condition={gridToolbarType === "all" || gridToolbarType === "link-modal"}>
            <ModalGridToolbar
              className="phase-action-grid"
              onQuichSearchInputChange={e => {
                gridRef?.current?.setSearchText(e.currentTarget.value);
              }}
              selectedItems={gridModel.selectedItems}
              preSelectedItemIds={preSelectedItemIds}
              onAssignSuccess={onAssignSuccess}
            />
          </IF>
          <IF
            condition={
              gridModel.userCanEditPhases &&
              (gridToolbarType === "all" || (gridToolbarType === "base" && !!gridModel.gridActions))
            }
          >
            <AppGridToolbar
              connectedUsers={connectedUsers}
              onQuichSearchInputChange={e => {
                gridRef.current.setSearchText(e.currentTarget.value);
              }}
              selectedItemCount={gridModel.selectedItems?.length}
              actions={gridModel.gridActions}
            />
          </IF>
          <AppGrid
            ref={gridRef}
            gridOptions={{ context: { deleteFn: gridModel.deleteFieldData }, ...gridModel.gridOptions }}
            filterStoreAddon={gridModel.filterStoreAddon}
            columnStateAddon={gridModel.columnStateAddon}
            textWrapperAddon={gridModel.textWrapAddon}
            onGridStateUpdate={gridModel.onGridStateUpdate}
            onGridReady={gridModel.onGridReady}
            rowData={data}
            columnDefs={gridModel.gridColumns}
            shouldTrackSelectedCell={true}
            connectedUsers={connectedUsers}
            noRowsOverlayComponent={showAudiences && EmptyPhaseGridTemplate}
            noRowsOverlayComponentParams={{
              link: Pages.projects.settings.phases.generateLink(organisationId, projectId),
              showLink: showAudiences
            }}
            onCellClicked={onCellStateChange && (onCellStateChange(organisationId, +projectId) as any)}
            onCellEditModeChange={onCellStateChange && (onCellStateChange(organisationId, +projectId) as any)}
            rowSelection="multiple" // Options - allows click selection of rows
            detailRowAutoHeight={true}
            detailRowHeight={500}
            masterDetail={true}
            detailCellRenderer={"phaseDetailCellRenderer"}
            components={{
              phaseDetailCellRenderer: PhaseDetailCellRenderer
            }}
            detailCellRendererParams={{
              refreshStrategy: "rows",
              showAudiences: !showAudiences,
              masterRefreshDataHandler: refreshDataHandler
            }}
          />
        </IF>
      </>
    );
  }
);
